import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Download, Medal, Trophy } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { PAGE_SEO } from '../../config/seoConfig';
import { useAuth } from '../../contexts/AuthContext';
import scholarshipService from '../../services/scholarship.service';
import {
  ScholarshipFinalResults,
  ScholarshipRegistration,
} from '../../types/scholarship';
import {
  buildScholarshipCertificateDesignData,
  downloadCertificateHtml,
} from '../../utils/studentCertificateDesign';

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message[0]) return String(message[0]);
  return fallback;
};

const ScholarshipResultsPage: React.FC = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [results, setResults] = useState<ScholarshipFinalResults | null>(null);
  const [registrations, setRegistrations] = useState<ScholarshipRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [certificateLoadingId, setCertificateLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      scholarshipService.getFinalResults(),
      currentUser ? scholarshipService.getMyRegistrations() : Promise.resolve([]),
    ])
      .then(([resultsResponse, registrationsResponse]) => {
        if (!mounted) return;
        if (resultsResponse.status === 'fulfilled') setResults(resultsResponse.value);
        if (registrationsResponse.status === 'fulfilled' && Array.isArray(registrationsResponse.value)) {
          setRegistrations(registrationsResponse.value);
        }
      })
      .catch((err) => {
        console.error('Failed to load scholarship results', err);
        if (mounted) setError('Unable to load scholarship results.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const downloadCertificate = async (registration: ScholarshipRegistration) => {
    try {
      setCertificateLoadingId(registration.registrationId);
      setError(null);
      const certificate = await scholarshipService.getCertificate(registration.registrationId);
      downloadCertificateHtml(buildScholarshipCertificateDesignData(certificate));
    } catch (err: any) {
      setError(getErrorMessage(err, 'Certificate is not available yet.'));
    } finally {
      setCertificateLoadingId(null);
    }
  };

  const renderWithSeo = (content: React.ReactElement) => (
    <>
      <SEO
        title={PAGE_SEO.SCHOLARSHIP_RESULTS.title}
        description={PAGE_SEO.SCHOLARSHIP_RESULTS.description}
        path={PAGE_SEO.SCHOLARSHIP_RESULTS.path}
        robots="noindex, follow"
      />
      {content}
    </>
  );

  if (loading) {
    return renderWithSeo(
      <div className="min-h-screen px-4 py-16 text-center text-slate-600">
        Loading scholarship results...
      </div>,
    );
  }

  return renderWithSeo(
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_50%,_#fff7ed_100%)] text-slate-950">
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
                <Trophy className="h-4 w-4" />
                Final Results
              </span>
              <h1 className="mt-5 text-4xl font-semibold tracking-tight text-slate-950">
                {results?.campaign?.title || 'Scholarship Results'}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                {results?.declared
                  ? 'Winners have been declared by the admin team.'
                  : 'Final winners will appear here after declaration.'}
              </p>
            </div>
            <Link
              to="/scholarship"
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Scholarship page
            </Link>
          </div>
        </div>

        {error && <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

        <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-950">Winners</h2>
            {results?.winners?.length ? (
              <div className="mt-5 space-y-4">
                {results.winners.map((winner) => (
                  <article key={winner.registrationId} className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="rounded-lg bg-amber-100 p-3 text-amber-700">
                          <Medal className="h-6 w-6" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-amber-700">Rank {winner.finalRank}</p>
                          <h3 className="mt-1 text-xl font-semibold text-slate-950">{winner.studentName}</h3>
                          <p className="mt-1 text-sm text-slate-500">
                            {winner.schoolName || 'School not listed'} | {winner.percentageScore || 0}%
                          </p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-amber-200 bg-white px-4 py-3 text-right">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">{winner.prizeTitle}</p>
                        <p className="mt-1 text-lg font-semibold text-slate-950">{formatCurrency(winner.prizeAmount)}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                Winner list is not declared yet.
              </div>
            )}
          </section>

          <aside className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-cyan-50 p-2 text-cyan-700">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-950">My Scholarship Status</h2>
                <p className="text-sm text-slate-500">Certificates are available after exam participation.</p>
              </div>
            </div>

            {!currentUser ? (
              <Link
                to="/login"
                className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700"
              >
                Sign in
              </Link>
            ) : registrations.length ? (
              <div className="mt-5 space-y-3">
                {registrations.map((registration) => (
                  <div key={registration.registrationId} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-950">{registration.scholarshipTitle}</p>
                    <p className="mt-1 text-xs text-slate-500">{registration.status}</p>
                    <p className="mt-2 text-sm text-slate-700">
                      Score {registration.percentageScore || 0}% {registration.rank ? `| Rank ${registration.rank}` : ''}
                    </p>
                    <button
                      type="button"
                      onClick={() => void downloadCertificate(registration)}
                      disabled={certificateLoadingId === registration.registrationId}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="h-4 w-4" />
                      {certificateLoadingId === registration.registrationId ? 'Preparing...' : 'Download certificate'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                No scholarship registration is linked to this account.
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>,
  );
};

export default ScholarshipResultsPage;
